import React, { useMemo, useState } from "react";
import { LuMail, LuX } from "react-icons/lu";

import { SetupCard } from "../../../../../components/newUI/setup-card";
import { getLinkedFirm } from "../../../../../sessionStorage/sessionStorage";
import {
  useGetSequenceOptionsQuery,
  useGetSequenceTemplatesQuery,
} from "../../../../../slices/EmailSequencesSlice";
import { useGetEmailTemplatesQuery } from "../../../../../slices/EmailTemplatesSlice";
import { SequenceForm } from "../../../../work/pages/sequences/emailSequence.types";
import {
  describeSequence,
  getDefaultForm,
} from "../../../../work/pages/sequences/emailSequence.utils";
import { SequenceModal } from "../../../../work/pages/sequences/SequenceModal";
import { useSequenceUsers } from "../../../../work/pages/sequences/WorkSequences";

type Props = {
  draft: SequenceForm | null;
  onChange: (draft: SequenceForm | null) => void;
  /** First selected contact and work type, for addresses and statuses. */
  customerId?: string;
  serviceId?: string;
  recipients: string[];
  currentUserId?: string;
};

/** New work window: an email sequence that starts once the work is created. */
export const AddWorkSequenceSection: React.FC<Props> = ({
  draft,
  onChange,
  customerId,
  serviceId,
  recipients,
  currentUserId,
}) => {
  const orgId = getLinkedFirm()?.orgId || "";
  const [isOpen, setIsOpen] = useState(false);
  const { data: options, isFetching } = useGetSequenceOptionsQuery(
    { orgId, customerId, serviceId },
    { skip: !orgId || !isOpen },
  );
  const { data: emailTemplates = [] } = useGetEmailTemplatesQuery(
    { orgId },
    { skip: !orgId },
  );
  const { data: sequenceTemplates = [] } = useGetSequenceTemplatesQuery(
    { orgId },
    { skip: !orgId || !isOpen },
  );
  const users = useSequenceUsers(isOpen ? orgId : "");

  const templateNames = useMemo(
    () =>
      Object.fromEntries(
        emailTemplates.map((template) => [template.id, template.name]),
      ),
    [emailTemplates],
  );

  const summary = draft
    ? describeSequence(
        {
          toEmail:
            recipients.length > 1
              ? "each contact's primary email"
              : draft.toEmail || "the contact's primary email",
          stopScope: draft.stopScope,
          steps: draft.steps.map((step, index) => ({
            order: index + 1,
            emailTemplateId: step.emailTemplateId,
            subject: step.subject,
            body: step.body,
            sendMode: index === 0 ? step.sendMode : "afterDays",
            delayDays: step.delayDays,
            scheduledDate: step.scheduledDate,
          })),
        },
        templateNames,
      )
        .map((part) => part.text)
        .join("")
    : "";

  return (
    <div className={"border-t border-[#ECEFF3] pt-[18px]"}>
      {draft ? (
        <div
          role={"button"}
          tabIndex={0}
          onClick={() => setIsOpen(true)}
          onKeyDown={(event) => {
            if (event.key === "Enter") setIsOpen(true);
          }}
          className={
            "flex cursor-pointer gap-3 rounded-[11px] border border-l-[3px] border-[#E4E7EC] border-l-[#6B4EFF] bg-white px-4 py-3 text-left"
          }>
          <span
            className={
              "flex h-9 w-9 shrink-0 items-center justify-center rounded-[9px] bg-[#F0EDFF] text-[#6B4EFF]"
            }>
            <LuMail className={"h-[18px] w-[18px]"} />
          </span>
          <div className={"min-w-0 flex-1"}>
            <div className={"text-[14px] font-bold text-[#1F2433]"}>
              {draft.name}
            </div>
            <div
              className={"mt-0.5 text-[12.5px] leading-[1.45] text-[#5B6678]"}>
              {summary}
            </div>
            <div className={"mt-1 text-[12px] text-[#8A90A2]"}>
              Will start when this work is created
            </div>
          </div>
          <button
            type={"button"}
            aria-label={"Remove email sequence"}
            onClick={(event) => {
              event.stopPropagation();
              onChange(null);
            }}
            className={"self-start text-[#A3A9B8] hover:text-[#E5484D]"}>
            <LuX size={16} />
          </button>
        </div>
      ) : (
        <SetupCard
          icon={<LuMail className={"h-[18px] w-[18px]"} />}
          title={"Add an email sequence"}
          description={"Automatically email the client when this work starts."}
          disabled={!customerId}
          onClick={() => setIsOpen(true)}
        />
      )}
      {!customerId && !draft && (
        <div className={"mt-1.5 text-[12px] text-[#8A90A2]"}>
          Pick a contact first.
        </div>
      )}

      {isOpen && (
        <SequenceModal
          mode={"draft"}
          initialForm={
            draft ||
            getDefaultForm({
              name: "Email sequence",
              fromUserId: currentUserId || "",
              toEmail: "",
            })
          }
          options={options}
          isLoadingOptions={isFetching}
          emailTemplates={emailTemplates}
          sequenceTemplates={sequenceTemplates}
          users={users}
          recipients={recipients}
          onClose={() => setIsOpen(false)}
          onSubmit={(form) => {
            onChange(form);
            setIsOpen(false);
          }}
        />
      )}
    </div>
  );
};
